import api from './api';

/**
 * Advanced Analytics Service
 * Handles all API calls for the Advanced Analytics & Alerts module
 */

// ============================================
// ALERT RULES API
// ============================================

/**
 * Get all alert rules
 * @param {Object} params - Query parameters (alert_type, severity, is_active)
 * @returns {Promise} Alert rules list
 */
export const getAlertRules = async (params = {}) => {
  const response = await api.get('/advanced-analytics/alert-rules/', { params });
  return response.data;
};

/**
 * Get a single alert rule by ID
 * @param {number} id - Alert rule ID
 * @returns {Promise} Alert rule details
 */
export const getAlertRule = async (id) => {
  const response = await api.get(`/advanced-analytics/alert-rules/${id}/`);
  return response.data;
};

/**
 * Create a new alert rule
 * @param {Object} data - Alert rule data
 * @returns {Promise} Created alert rule
 */
export const createAlertRule = async (data) => {
  const response = await api.post('/advanced-analytics/alert-rules/', data);
  return response.data;
};

/**
 * Update an existing alert rule
 * @param {number} id - Alert rule ID
 * @param {Object} data - Updated alert rule data
 * @returns {Promise} Updated alert rule
 */
export const updateAlertRule = async (id, data) => {
  const response = await api.put(`/advanced-analytics/alert-rules/${id}/`, data);
  return response.data;
};

/**
 * Partially update an alert rule
 * @param {number} id - Alert rule ID
 * @param {Object} data - Partial alert rule data
 * @returns {Promise} Updated alert rule
 */
export const patchAlertRule = async (id, data) => {
  const response = await api.patch(`/advanced-analytics/alert-rules/${id}/`, data);
  return response.data;
};

/**
 * Delete an alert rule
 * @param {number} id - Alert rule ID
 * @returns {Promise}
 */
export const deleteAlertRule = async (id) => {
  const response = await api.delete(`/advanced-analytics/alert-rules/${id}/`);
  return response.data;
};

/**
 * Test an alert rule without saving
 * @param {number} id - Alert rule ID
 * @returns {Promise} Test result
 */
export const testAlertRule = async (id) => {
  const response = await api.post(`/advanced-analytics/alert-rules/${id}/test/`);
  return response.data;
};

// ============================================
// ALERT INSTANCES API
// ============================================

/**
 * Get all alert instances
 * @param {Object} params - Query parameters (status, alert_rule, assigned_to)
 * @returns {Promise} Alert instances list
 */
export const getAlertInstances = async (params = {}) => {
  const response = await api.get('/advanced-analytics/alert-instances/', { params });
  return response.data;
};

/**
 * Get a single alert instance by ID
 * @param {number} id - Alert instance ID
 * @returns {Promise} Alert instance details
 */
export const getAlertInstance = async (id) => {
  const response = await api.get(`/advanced-analytics/alert-instances/${id}/`);
  return response.data;
};

/**
 * Create a new alert instance (usually done by backend)
 * @param {Object} data - Alert instance data
 * @returns {Promise} Created alert instance
 */
export const createAlertInstance = async (data) => {
  const response = await api.post('/advanced-analytics/alert-instances/', data);
  return response.data;
};

/**
 * Update an alert instance
 * @param {number} id - Alert instance ID
 * @param {Object} data - Updated alert instance data
 * @returns {Promise} Updated alert instance
 */
export const updateAlertInstance = async (id, data) => {
  const response = await api.put(`/advanced-analytics/alert-instances/${id}/`, data);
  return response.data;
};

/**
 * Acknowledge an alert
 * @param {number} id - Alert instance ID
 * @returns {Promise} Updated alert instance
 */
export const acknowledgeAlert = async (id) => {
  const response = await api.post(`/advanced-analytics/alert-instances/${id}/acknowledge/`);
  return response.data;
};

/**
 * Snooze an alert
 * @param {number} id - Alert instance ID
 * @param {number} minutes - Snooze duration in minutes (default: 30)
 * @returns {Promise} Updated alert instance
 */
export const snoozeAlert = async (id, minutes = 30) => {
  const response = await api.post(`/advanced-analytics/alert-instances/${id}/snooze/`, { minutes });
  return response.data;
};

/**
 * Resolve an alert
 * @param {number} id - Alert instance ID
 * @param {string} notes - Resolution notes
 * @param {string} action - Resolution action taken
 * @returns {Promise} Updated alert instance
 */
export const resolveAlert = async (id, notes = '', action = '') => {
  const response = await api.post(`/advanced-analytics/alert-instances/${id}/resolve/`, { notes, action });
  return response.data;
};

/**
 * Delete an alert instance
 * @param {number} id - Alert instance ID
 * @returns {Promise}
 */
export const deleteAlertInstance = async (id) => {
  const response = await api.delete(`/advanced-analytics/alert-instances/${id}/`);
  return response.data;
};

// ============================================
// NOTIFICATION PREFERENCES API
// ============================================

/**
 * Get all notification preferences (admin only)
 * @returns {Promise} Notification preferences list
 */
export const getNotificationPreferences = async () => {
  const response = await api.get('/advanced-analytics/notification-preferences/');
  return response.data;
};

/**
 * Get a single notification preference by ID
 * @param {number} id - Notification preference ID
 * @returns {Promise} Notification preference details
 */
export const getNotificationPreference = async (id) => {
  const response = await api.get(`/advanced-analytics/notification-preferences/${id}/`);
  return response.data;
};

/**
 * Get current user's notification preferences
 * @returns {Promise} Current user's notification preferences
 */
export const getMyNotificationPreferences = async () => {
  const response = await api.get('/advanced-analytics/notification-preferences/my_preferences/');
  return response.data;
};

/**
 * Update current user's notification preferences
 * @param {Object} data - Updated notification preferences
 * @returns {Promise} Updated notification preferences
 */
export const updateMyNotificationPreferences = async (data) => {
  const response = await api.put('/advanced-analytics/notification-preferences/my_preferences/', data);
  return response.data;
};

/**
 * Create notification preferences for a user
 * @param {Object} data - Notification preferences data
 * @returns {Promise} Created notification preferences
 */
export const createNotificationPreference = async (data) => {
  const response = await api.post('/advanced-analytics/notification-preferences/', data);
  return response.data;
};

/**
 * Update notification preferences
 * @param {number} id - Notification preference ID
 * @param {Object} data - Updated notification preferences
 * @returns {Promise} Updated notification preferences
 */
export const updateNotificationPreference = async (id, data) => {
  const response = await api.put(`/advanced-analytics/notification-preferences/${id}/`, data);
  return response.data;
};

/**
 * Delete notification preferences
 * @param {number} id - Notification preference ID
 * @returns {Promise}
 */
export const deleteNotificationPreference = async (id) => {
  const response = await api.delete(`/advanced-analytics/notification-preferences/${id}/`);
  return response.data;
};

// ============================================
// SAVED ANALYTICS VIEWS API
// ============================================

/**
 * Get all saved analytics views (user's own + shared views)
 * @param {Object} params - Query parameters (tab, is_shared, is_default)
 * @returns {Promise} Saved views list
 */
export const getSavedViews = async (params = {}) => {
  const response = await api.get('/advanced-analytics/saved-views/', { params });
  return response.data;
};

/**
 * Get a single saved view by ID
 * @param {number} id - Saved view ID
 * @returns {Promise} Saved view details
 */
export const getSavedView = async (id) => {
  const response = await api.get(`/advanced-analytics/saved-views/${id}/`);
  return response.data;
};

/**
 * Create a new saved view
 * @param {Object} data - Saved view data
 * @returns {Promise} Created saved view
 */
export const createSavedView = async (data) => {
  const response = await api.post('/advanced-analytics/saved-views/', data);
  return response.data;
};

/**
 * Update a saved view
 * @param {number} id - Saved view ID
 * @param {Object} data - Updated saved view data
 * @returns {Promise} Updated saved view
 */
export const updateSavedView = async (id, data) => {
  const response = await api.put(`/advanced-analytics/saved-views/${id}/`, data);
  return response.data;
};

/**
 * Partially update a saved view
 * @param {number} id - Saved view ID
 * @param {Object} data - Partial saved view data
 * @returns {Promise} Updated saved view
 */
export const patchSavedView = async (id, data) => {
  const response = await api.patch(`/advanced-analytics/saved-views/${id}/`, data);
  return response.data;
};

/**
 * Delete a saved view
 * @param {number} id - Saved view ID
 * @returns {Promise}
 */
export const deleteSavedView = async (id) => {
  const response = await api.delete(`/advanced-analytics/saved-views/${id}/`);
  return response.data;
};

/**
 * Set a saved view as default for the user
 * @param {number} id - Saved view ID
 * @returns {Promise} Updated saved view
 */
export const setDefaultView = async (id) => {
  const response = await api.post(`/advanced-analytics/saved-views/${id}/set_default/`);
  return response.data;
};

// ============================================
// ANALYTICS HELPER FUNCTIONS
// ============================================

/**
 * Get active alerts count by severity
 * @returns {Promise} Alert counts by severity
 */
export const getActiveAlertCounts = async () => {
  const instances = await getAlertInstances({ status: 'ACTIVE' });

  const counts = {
    CRITICAL: 0,
    HIGH: 0,
    MEDIUM: 0,
    LOW: 0,
    total: 0
  };

  if (Array.isArray(instances)) {
    instances.forEach(instance => {
      const severity = instance.alert_severity;
      if (counts.hasOwnProperty(severity)) {
        counts[severity]++;
      }
      counts.total++;
    });
  }

  return counts;
};

/**
 * Get alert statistics
 * @param {Object} params - Query parameters
 * @returns {Promise} Alert statistics
 */
export const getAlertStatistics = async (params = {}) => {
  const instances = await getAlertInstances(params);

  const stats = {
    total: 0,
    active: 0,
    acknowledged: 0,
    resolved: 0,
    snoozed: 0,
    escalated: 0,
    byType: {},
    bySeverity: {}
  };

  if (Array.isArray(instances)) {
    instances.forEach(instance => {
      stats.total++;

      // Count by status
      const status = instance.status?.toLowerCase();
      if (stats.hasOwnProperty(status)) {
        stats[status]++;
      }

      // Count by type
      const type = instance.alert_rule?.alert_type;
      if (type) {
        stats.byType[type] = (stats.byType[type] || 0) + 1;
      }

      // Count by severity
      const severity = instance.alert_severity;
      if (severity) {
        stats.bySeverity[severity] = (stats.bySeverity[severity] || 0) + 1;
      }
    });
  }

  return stats;
};

/**
 * Export analytics data
 * @param {string} tab - Active tab
 * @param {Object} filters - Applied filters
 * @param {string} dateRange - Date range
 * @returns {Object} Export data
 */
export const exportAnalyticsData = (tab, filters, dateRange) => {
  return {
    tab,
    filters,
    dateRange,
    exportedAt: new Date().toISOString(),
    exportedBy: localStorage.getItem('user_id') || 'unknown'
  };
};

// ============================================
// OPERATIONS ANALYTICS API
// ============================================

/**
 * Get operations overview analytics
 * @param {Object} params - Query parameters (days, operations, suppliers, materials)
 * @returns {Promise} Operations analytics data
 */
export const getOperationsOverview = async (params = {}) => {
  const response = await api.get('/advanced-analytics/operations/operations_overview/', { params });
  return response.data;
};

/**
 * Get bottleneck heatmap data
 * @param {Object} params - Query parameters (days)
 * @returns {Promise} Heatmap data
 */
export const getBottleneckHeatmap = async (params = {}) => {
  const response = await api.get('/advanced-analytics/operations/bottleneck_heatmap/', { params });
  return response.data;
};

/**
 * Get performance trends
 * @param {Object} params - Query parameters (days, interval)
 * @returns {Promise} Trend data
 */
export const getPerformanceTrends = async (params = {}) => {
  const response = await api.get('/advanced-analytics/operations/performance_trends/', { params });
  return response.data;
};

// ============================================
// SUPPLIER ANALYTICS API
// ============================================

/**
 * Get supplier scorecard
 * @param {Object} params - Query parameters (days, suppliers)
 * @returns {Promise} Supplier scorecard data
 */
export const getSupplierScorecard = async (params = {}) => {
  const response = await api.get('/advanced-analytics/suppliers/supplier_scorecard/', { params });
  return response.data;
};

/**
 * Get supplier performance trends
 * @param {Object} params - Query parameters (days, supplier_code)
 * @returns {Promise} Supplier trends data
 */
export const getSupplierTrends = async (params = {}) => {
  const response = await api.get('/advanced-analytics/suppliers/supplier_trends/', { params });
  return response.data;
};

// ============================================
// SLA ANALYTICS API
// ============================================

/**
 * Get SLA compliance overview
 * @param {Object} params - Query parameters (days)
 * @returns {Promise} SLA overview data
 */
export const getSLAOverview = async (params = {}) => {
  const response = await api.get('/advanced-analytics/sla/sla_overview/', { params });
  return response.data;
};

/**
 * Get SLA breach analysis
 * @param {Object} params - Query parameters (days)
 * @returns {Promise} SLA breach analysis data
 */
export const getSLABreachAnalysis = async (params = {}) => {
  const response = await api.get('/advanced-analytics/sla/breach_analysis/', { params });
  return response.data;
};

// ============================================
// MATERIAL ANALYTICS API
// ============================================

/**
 * Get material analytics overview with ABC classification
 * @param {Object} params - Query parameters (days)
 * @returns {Promise} Material analytics data
 */
export const getMaterialOverview = async (params = {}) => {
  const response = await api.get('/advanced-analytics/materials/material_overview/', { params });
  return response.data;
};

/**
 * Get material turnover analysis
 * @param {Object} params - Query parameters (days)
 * @returns {Promise} Material turnover data
 */
export const getMaterialTurnover = async (params = {}) => {
  const response = await api.get('/advanced-analytics/materials/turnover_analysis/', { params });
  return response.data;
};

// ============================================
// COST ANALYTICS API
// ============================================

/**
 * Get cost overview analytics
 * @param {Object} params - Query parameters (days)
 * @returns {Promise} Cost overview data
 */
export const getCostOverview = async (params = {}) => {
  const response = await api.get('/advanced-analytics/costs/cost_overview/', { params });
  return response.data;
};

/**
 * Get cost comparison (period-over-period)
 * @param {Object} params - Query parameters (days)
 * @returns {Promise} Cost comparison data
 */
export const getCostComparison = async (params = {}) => {
  const response = await api.get('/advanced-analytics/costs/cost_comparison/', { params });
  return response.data;
};

// ============================================
// ALERTS DASHBOARD API
// ============================================

/**
 * Get alerts dashboard overview
 * @returns {Promise} Alerts overview data
 */
export const getAlertsDashboardOverview = async () => {
  const response = await api.get('/advanced-analytics/alerts-dashboard/alerts_overview/');
  return response.data;
};

/**
 * Get alerts trend over time
 * @param {Object} params - Query parameters (days)
 * @returns {Promise} Alerts trend data
 */
export const getAlertsTrend = async (params = {}) => {
  const response = await api.get('/advanced-analytics/alerts-dashboard/alerts_trend/', { params });
  return response.data;
};

export default {
  // Alert Rules
  getAlertRules,
  getAlertRule,
  createAlertRule,
  updateAlertRule,
  patchAlertRule,
  deleteAlertRule,
  testAlertRule,

  // Alert Instances
  getAlertInstances,
  getAlertInstance,
  createAlertInstance,
  updateAlertInstance,
  acknowledgeAlert,
  snoozeAlert,
  resolveAlert,
  deleteAlertInstance,

  // Notification Preferences
  getNotificationPreferences,
  getNotificationPreference,
  getMyNotificationPreferences,
  updateMyNotificationPreferences,
  createNotificationPreference,
  updateNotificationPreference,
  deleteNotificationPreference,

  // Saved Views
  getSavedViews,
  getSavedView,
  createSavedView,
  updateSavedView,
  patchSavedView,
  deleteSavedView,
  setDefaultView,

  // Operations Analytics
  getOperationsOverview,
  getBottleneckHeatmap,
  getPerformanceTrends,

  // Supplier Analytics
  getSupplierScorecard,
  getSupplierTrends,

  // SLA Analytics
  getSLAOverview,
  getSLABreachAnalysis,

  // Material Analytics
  getMaterialOverview,
  getMaterialTurnover,

  // Cost Analytics
  getCostOverview,
  getCostComparison,

  // Alerts Dashboard
  getAlertsDashboardOverview,
  getAlertsTrend,

  // Helper Functions
  getActiveAlertCounts,
  getAlertStatistics,
  exportAnalyticsData
};
